import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { shallow } from 'zustand/shallow';
import styles from "./login.module.css"
// import appLogo from "../../assets/images/agencia_nacional_digital.png";
import iconPassView from "../../assets/login/icon-pass-view.svg";
import iconPass from "../../assets/login/icon-pass.svg";
import iconPerson from "../../assets/login/icon-person.svg";
import loginGif from "../../assets/images/Kami-Saludo2.gif";
import { useAuth } from "../../contexts/AuthContext";
import { useUserStore } from "../../store/user.store";
import LoadingInformation from "../../common/UI/LoadingInformation";
import { LoginService } from "../services/loginByrut.service";
import { toast } from "react-toastify";
import AppGetIcon from "../../common/UI/AppGetIcon";
import ProgressBar from "../../common/UI/ProgressBar";

const loginService = new LoginService();

type LoginForm = {
  textUser: string;
  textPass: string;
};

type LoginFieldErrors = Record<keyof LoginForm, string>;

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<LoginForm>({ textUser: "", textPass: "" });
  const [fieldErrors, setFieldErrors] = useState<LoginFieldErrors>({ textUser: "", textPass: "" });
  const [error, setError] = useState("");
  const [messageDescription, setMessageDescription] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [errorType, setErrorType] = useState(null);
  const navigate = useNavigate();
  const { login } = useAuth();


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    const fieldName = id as keyof LoginForm;

    setForm({ ...form, [fieldName]: value });

    if (fieldErrors[fieldName]) {
      setFieldErrors({ ...fieldErrors, [fieldName]: "" });
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setErrorType(null);
    setMessageDescription("");
  };

  const showLoginError = (errorMessage: string) => {
    setError(errorMessage);
    toast.error(
      <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
        <AppGetIcon name="xCircleFill" />
        {errorMessage}
      </div>,
      { icon: false }
    );
  };

  const validateForm = (): LoginFieldErrors => ({
    textUser: form.textUser.trim() ? "" : "El usuario es obligatorio",
    textPass: form.textPass.trim() ? "" : "La contraseña es obligatoria",
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const nextFieldErrors = validateForm();
    const hasFieldErrors = Object.values(nextFieldErrors).some(Boolean);

    if (hasFieldErrors) {
      setError("");
      setFieldErrors(nextFieldErrors);
      return;
    }

    setFieldErrors({ textUser: "", textPass: "" });
    setLoading(true);
    setError("");
    try {
      const response = await loginService.run({ email: form.textUser.trim(), password: form.textPass });
      const accessToken = response.accessToken;
      const refreshToken = response.refreshToken; // Si tu API lo devuelve

      localStorage.setItem('authToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);

      // Guardar token y el estado de autenticación en cookies
      login(accessToken);

      toast.success(
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <AppGetIcon name="checkCircleFill" />
          {response.responseServiceDescription ?? 'Bienvenido de nuevo!'}
        </div>,
        { icon: false }
      );
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || "Error en el inicio de sesión";
      showLoginError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className={styles.login}>
      {loading && <ProgressBar />}

      <aside className={styles.loginAside}>
        <div className={styles.asideContent}>
          <img className={styles.asideGif} src={loginGif} alt="Saludo Kami" />
          <h2>Impulsa tu aprendizaje</h2>
          <p>Gestiona tu futuro académico con un solo clic.</p>
        </div>
      </aside>

      <main className={styles.loginMain}>
        <div className={styles.loginCard}>
          <header className={styles.loginHeader}>
            <Link to="/">
              <img
                className={styles.brandLogo}
                src="https://portal.kamilainnovation.com.co/wp-content/uploads/2022/10/Logo-KINN.png"
                alt="App Logo"
              />
            </Link>
            <h1 className={styles.title}>¡Bienvenido de nuevo!</h1>
            <p className={styles.subtitle}>Ingresa tus credenciales para acceder</p>
            {error && (
              <div className={`${styles.errorMessage} ${styles.errorBanner}`}>
                {error}
              </div>
            )}
          </header>

          <form onSubmit={handleSubmit} className={styles.loginForm} noValidate>
            <div className={styles.inputGroup}>
              <div className={styles.inputRelative}>
                <input
                  type="text"
                  id="textUser"
                  placeholder=" "
                  className={`${styles.customInput} ${fieldErrors.textUser ? styles.customInputError : ""}`}
                  value={form.textUser}
                  onChange={handleInputChange}
                  required
                  aria-required="true"
                  aria-invalid={Boolean(fieldErrors.textUser)}
                  aria-describedby={fieldErrors.textUser ? "textUser-error" : undefined}
                  autoComplete="username"
                />
                <label htmlFor="textUser" className={styles.customLabel}>
                  Usuario
                </label>
                <span className={styles.inputIcon}>
                  <img src={iconPerson} alt="Usuario" />
                </span>
              </div>
              {fieldErrors.textUser && (
                <p id="textUser-error" className={styles.fieldError}>
                  {fieldErrors.textUser}
                </p>
              )}
            </div>

            <div className={styles.inputGroup}>
              <div className={styles.inputRelative}>
                <input
                  type={showPassword ? "text" : "password"}
                  id="textPass"
                  placeholder=" "
                  className={`${styles.customInput} ${fieldErrors.textPass ? styles.customInputError : ""}`}
                  value={form.textPass}
                  onChange={handleInputChange}
                  required
                  aria-required="true"
                  aria-invalid={Boolean(fieldErrors.textPass)}
                  aria-describedby={fieldErrors.textPass ? "textPass-error" : undefined}
                  autoComplete="current-password"
                />
                <label htmlFor="textPass" className={styles.customLabel}>
                  Contraseña
                </label>
                <button
                  type="button"
                  className={`${styles.inputIcon} ${styles.inputIconBtn}`}
                  onClick={togglePasswordVisibility}
                >
                  <img
                    src={showPassword ? iconPassView : iconPass}
                    alt={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  />
                </button>
              </div>
              {fieldErrors.textPass && (
                <p id="textPass-error" className={styles.fieldError}>
                  {fieldErrors.textPass}
                </p>
              )}
            </div>

            <div className={styles.formOptions}>
              <label className={styles.checkboxContainer} htmlFor="Remember">
                <input type="checkbox" id="Remember" />
                <span className={styles.checkmark}></span>
                Recuérdame
              </label>
              <Link className={styles.forgotLink} to="/remember-password">
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <button
              type="submit"
              className={`${styles.btnPrimary} ${loading ? styles.disableBtn : ""}`}
              disabled={loading}
            >
              Iniciar sesión
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
